
import { useState, useEffect, useCallback } from "react";
import { db } from "../../firebaseClient";
import { computeDashboardSummary, TodoItem } from "../services/todosSummary";
import type { DailySummaryResult } from "../services/dailySummaryService";

export type LiveStatus = "connecting" | "connected" | "offline" | "error" | "paused";

export interface LogEvent {
    time: string;
    type: "START" | "STOP" | "SNAPSHOT" | "PROBE" | "RETRY" | "ERROR" | "NETWORK";
    payload?: string;
}

// --- Global Singleton State ---
interface GlobalState {
    liveStatus: LiveStatus;
    liveMsg: string;
    data: DailySummaryResult | null;
    lastSnapshotAt: number | null;
    lastSnapshotFromCache: boolean | null;
    snapshotCount: number;
    lastError: { code?: string; message?: string } | null;
    retryAttempt: number;
    retryNextAt: number | null;
    isListening: boolean;
    networkOnline: boolean;
    probe: {
        lastProbeAt: number | null;
        ok: boolean | null;
        errorCode?: string;
        errorMessage?: string;
    };
    eventLog: LogEvent[];
}

const INITIAL_STATE: GlobalState = {
    liveStatus: "connecting",
    liveMsg: "初始化中...",
    data: null,
    lastSnapshotAt: null,
    lastSnapshotFromCache: null,
    snapshotCount: 0,
    lastError: null,
    retryAttempt: 0,
    retryNextAt: null,
    isListening: false,
    networkOnline: true,
    probe: { lastProbeAt: null, ok: null },
    eventLog: []
};

// Module-level variables (Singleton)
let currentState: GlobalState = { ...INITIAL_STATE };
let listeners: Array<(s: GlobalState) => void> = [];
let unsubRef: (() => void) | null = null;
let retryTimer: any = null;
let heartbeatTimer: any = null;

// --- Internal Helper: State Updates ---
function updateState(updates: Partial<GlobalState>) {
    currentState = { ...currentState, ...updates };
    listeners.forEach(listener => listener(currentState));
}

function addLog(type: LogEvent['type'], payloadObj?: any) {
    const entry: LogEvent = {
        time: new Date().toLocaleTimeString(),
        type,
        payload: payloadObj ? (typeof payloadObj === 'string' ? payloadObj : JSON.stringify(payloadObj)) : undefined
    };
    const newLog = [entry, ...currentState.eventLog].slice(0, 200);
    updateState({ eventLog: newLog });
    console.log(`[TodosLiveMonitor] ${type}`, payloadObj || "");
}

// --- Internal Helper: Probe ---
async function performProbe(silent = false) {
    if (!silent) addLog("PROBE", "Starting server probe...");
    const start = Date.now();
    try {
        await db.collection("todos").limit(1).get({ source: 'server' });
        updateState({ 
            probe: { lastProbeAt: Date.now(), ok: true }
        });
        if (!silent) addLog("PROBE", `Success (${Date.now() - start}ms)`);
        
        // If we were in error state, recover
        if (currentState.liveStatus === 'error' || currentState.liveStatus === 'offline') {
            updateState({ liveStatus: 'connected', liveMsg: '連線正常 (Probe Recovered)' });
        }
    } catch (e: any) {
        updateState({ 
            probe: { 
                lastProbeAt: Date.now(), 
                ok: false, 
                errorCode: e.code, 
                errorMessage: e.message 
            }
        });
        if (!silent) addLog("PROBE", `Failed: ${e.message}`);
        
        // If explicit probe fails, mark unstable
        if (!silent) {
            updateState({ 
                liveStatus: 'error', 
                liveMsg: `連線異常 (Probe Failed): ${e.code || 'Unknown'}` 
            });
            scheduleRetry();
        }
    }
}

// --- Internal Helper: Cleanup ---
function cleanup() {
    if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
    if (unsubRef) {
        unsubRef();
        unsubRef = null;
        addLog("STOP", "Listener Unsubscribed");
    }
    updateState({ isListening: false });
}

// --- Internal Helper: Schedule Retry ---
function scheduleRetry() {
    if (retryTimer) clearTimeout(retryTimer);
    
    const attempt = currentState.retryAttempt + 1;
    // Cap at 30s
    const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
    
    updateState({
        retryAttempt: attempt,
        retryNextAt: Date.now() + delay,
        liveStatus: 'connecting',
        liveMsg: `連線中斷，${Math.ceil(delay/1000)}秒後重試...`
    });
    
    addLog("RETRY", `Scheduling retry #${attempt} in ${delay}ms`);
    
    retryTimer = setTimeout(() => {
        addLog("RETRY", "Executing retry now...");
        startListener();
    }, delay);
}

// --- Internal Helper: Start Listener ---
function startListener() {
    if (unsubRef) return; // Already active

    cleanup(); // Safety cleanup
    updateState({ isListening: true, liveStatus: 'connecting', liveMsg: '建立連線中...' });
    addLog("START", "Setting up onSnapshot...");

    try {
        const query = db.collection("todos").where("todoStatus", "==", "OPEN").limit(200);
        
        unsubRef = query.onSnapshot({ includeMetadataChanges: true }, 
            (snap: any) => {
                const fromCache = snap.metadata.fromCache;
                const count = snap.size;
                
                updateState({
                    lastSnapshotAt: Date.now(),
                    lastSnapshotFromCache: fromCache,
                    snapshotCount: currentState.snapshotCount + 1
                });
                
                // --- Connection Logic (Critical Fix) ---
                // If not from cache, we are definitely connected.
                if (!fromCache) {
                    updateState({
                        liveStatus: 'connected',
                        liveMsg: '連線正常',
                        retryAttempt: 0,
                        retryNextAt: null,
                        lastError: null
                    });
                } else {
                    // From cache means potentially offline or just loading initial cache
                    // Do NOT error out, just show connecting/loading state if it persists
                    // But if we already were connected, this might mean temporary disconnect
                    if (currentState.liveStatus !== 'connected') {
                        updateState({
                            liveStatus: 'connecting',
                            liveMsg: '連線中 (讀取快取)...'
                        });
                    }
                }

                // Process Data
                const todos = snap.docs.map((doc: any) => ({
                    id: doc.id,
                    ...doc.data()
                })) as TodoItem[];
                
                const result = computeDashboardSummary(todos);
                updateState({ data: result });
            },
            (err: any) => {
                updateState({
                    liveStatus: 'error',
                    lastError: { code: err.code, message: err.message },
                    liveMsg: `監聽錯誤: ${err.code}`
                });
                addLog("ERROR", err.message);
                
                // Cleanup internal ref but keep state as error
                if (unsubRef) { unsubRef(); unsubRef = null; }
                updateState({ isListening: false });
                
                scheduleRetry();
            }
        );

        // Start Heartbeat (every 60s)
        if (!heartbeatTimer) {
            heartbeatTimer = setInterval(() => {
                if (currentState.liveStatus === 'connected') {
                    performProbe(true); // Silent probe to check health
                }
            }, 60000);
        }

    } catch (err: any) {
        addLog("ERROR", `Setup Failed: ${err.message}`);
        updateState({
            liveStatus: 'error',
            liveMsg: "無法建立監聽",
            lastError: { code: 'SETUP_FAILED', message: err.message }
        });
        updateState({ isListening: false });
    }
}

// --- Internal Helper: Reconnect ---
async function manualReconnect() {
    addLog("NETWORK", "Manual Reconnect Triggered");
    cleanup();
    updateState({ 
        liveStatus: 'connecting', 
        liveMsg: '正在重置網路並重新連線...',
        retryAttempt: 0,
        lastError: null
    });
    
    try {
        await db.disableNetwork();
        await new Promise(r => setTimeout(r, 500));
        await db.enableNetwork();
        addLog("NETWORK", "Network cycled");
        
        await performProbe(false); // Explicit probe
        startListener();
    } catch (e: any) {
        updateState({ liveStatus: 'error', liveMsg: '重連失敗' });
        addLog("ERROR", `Reconnect Failed: ${e.message}`);
    }
}

// --- Exported Hook ---
export function useTodosLiveMonitor() {
    const [state, setState] = useState<GlobalState>(currentState);

    useEffect(() => {
        // Subscribe to global state changes
        listeners.push(setState);
        
        // Auto-start if not running
        if (!unsubRef && !retryTimer) {
            startListener();
        }

        // Network listeners
        const onOnline = () => {
            updateState({ networkOnline: true });
            addLog("NETWORK", "Browser Online Event");
            if (currentState.liveStatus === 'offline' || currentState.liveStatus === 'error') {
                manualReconnect();
            }
        };
        const onOffline = () => {
            updateState({ networkOnline: false, liveStatus: 'offline', liveMsg: '網路已離線' });
            addLog("NETWORK", "Browser Offline Event");
        };

        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        updateState({ networkOnline: navigator.onLine });

        return () => {
            listeners = listeners.filter(l => l !== setState);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
            
            // Note: We do NOT stop the singleton listener when a component unmounts
            // This ensures Dashboard and Debug page share the same persistent connection.
            // Explicit stop() must be called via button if needed.
        };
    }, []);

    return {
        ...state,
        hasUnsub: !!unsubRef,
        start: startListener,
        stop: cleanup,
        reconnect: manualReconnect,
        probeServer: () => performProbe(false),
        clearError: () => updateState({ lastError: null })
    };
}
